import React, { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    CircularProgress,
} from '@material-ui/core';
import { ChevronDown, ChevronRight } from 'tabler-icons-react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { useReportes } from './ReportesContex';

const CHART_COLORS = [
    '#118DFF',
    '#fe424d',
    '#ffc800',
    '#2ccb9e',
    '#E91E63',
    '#f97316',
    '#06b6d4',
    '#84cc16',
    '#ec4899',
    '#14b8a6',
    '#0ea5e9',
    '#f59e0b',
    '#ef4444',
    '#10b981',
    '#34d399',
    '#3b82f6',
    '#0891B2',
    '#f43f5e',
    '#22c55e',
    '#eab308',
    '#64748b',
    '#fb923c',
    '#0369A1',
    '#4ade80',
    '#facc15',
    '#60a5fa',
    '#f472b6',
    '#22d3ee',
    '#84cc16',
    '#f97316',
];

function getColors(count) {
    const colors = [];
    for (let i = 0; i < count; i++) {
        colors.push(CHART_COLORS[i % CHART_COLORS.length]);
    }
    return colors;
}

// ── Utilidades de color ───────────────────────────────────────────────────────

function getPrimaryColor() {
    return getComputedStyle(document.documentElement)
        .getPropertyValue('--color')
        .trim()
        .replace('#', '');
}

function blendHex(hex, blendWith, ratio) {
    const parse = (h) => ({
        r: parseInt(h.substring(0, 2), 16),
        g: parseInt(h.substring(2, 4), 16),
        b: parseInt(h.substring(4, 6), 16),
    });
    const c1 = parse(hex.replace('#', ''));
    const c2 = parse(blendWith.replace('#', ''));
    const r = Math.round(c1.r * (1 - ratio) + c2.r * ratio);
    const g = Math.round(c1.g * (1 - ratio) + c2.g * ratio);
    const b = Math.round(c1.b * (1 - ratio) + c2.b * ratio);
    return [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

// ExcelJS usa formato ARGB (FF + hex sin #)
const toArgb = (hex) => `FF${hex.replace('#', '').toUpperCase()}`;

// ── Exportación a Excel ───────────────────────────────────────────────────────

async function exportarExcel(grouped, txtRange) {
    const primary = getPrimaryColor();
    const headerBg = toArgb(blendHex(primary, '000000', 0.2)); // oscurecido 20%
    const lineaBg = toArgb(primary); // color puro
    const turnoBg = toArgb(blendHex(primary, 'ffffff', 0.82)); // 18% primary
    const totalBg = toArgb(blendHex(primary, 'ffffff', 0.9)); // 10% primary
    const turnoText = toArgb(blendHex(primary, '000000', 0.2));
    const totalText = turnoText;

    const wb = new ExcelJS.Workbook();
    wb.creator = 'NOVA MES';
    const ws = wb.addWorksheet('Reporte de Producción');

    // Anchos de columna
    ws.columns = [
        { key: 'linea', width: 20 },
        { key: 'turno', width: 10 },
        { key: 'ordenProd', width: 15 },
        { key: 'sku', width: 18 },
        { key: 'producto', width: 40 },
        { key: 'piezas', width: 16 },
    ];

    const styleCell = (
        cell,
        { bgArgb, textArgb = 'FFFFFFFF', bold = false, size = 11, align = 'left' } = {}
    ) => {
        if (bgArgb) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgArgb } };
        }
        cell.font = { name: 'Calibri', size, bold, color: { argb: textArgb } };
        cell.alignment = { vertical: 'middle', horizontal: align, wrapText: false };
        cell.border = {
            bottom: { style: 'thin', color: { argb: 'FFE8ECF1' } },
        };
    };

    // ── Título ───────────────────────────────────────────────────────────────
    ws.mergeCells('A1:F1');
    const titleCell = ws.getCell('A1');
    titleCell.value = 'Reporte de Producción';
    styleCell(titleCell, { bgArgb: headerBg, bold: true, size: 14, align: 'center' });
    ws.getRow(1).height = 28;

    if (txtRange) {
        ws.mergeCells('A2:F2');
        const subCell = ws.getCell('A2');
        subCell.value = `Periodo: ${txtRange}`;
        styleCell(subCell, { bgArgb: headerBg, textArgb: 'FFCCCCdd', size: 10, align: 'center' });
        ws.getRow(2).height = 18;
    }

    // ── Encabezado de columnas ───────────────────────────────────────────────
    const headerRow = ws.addRow(['Linea', 'Turno', 'Orden Prod', 'SKU', 'Producto', 'Piezas Prod']);
    headerRow.eachCell((cell) => {
        styleCell(cell, { bgArgb: headerBg, bold: true, size: 11, align: 'center' });
        cell.border = {
            bottom: { style: 'medium', color: { argb: `FF${primary.toUpperCase()}` } },
        };
    });
    headerRow.height = 22;

    const grandTotal = grouped.reduce((acc, g) => acc + g.total, 0);

    // ── Datos ────────────────────────────────────────────────────────────────
    grouped.forEach((lineaGroup) => {
        // Fila de Línea
        const lRow = ws.addRow([lineaGroup.linea, '', '', '', '', lineaGroup.total]);
        lRow.eachCell((cell) => styleCell(cell, { bgArgb: lineaBg, bold: true, size: 11 }));
        lRow.getCell(6).alignment = { horizontal: 'right', vertical: 'middle' };
        lRow.getCell(6).numFmt = '#,##0';
        lRow.height = 20;

        lineaGroup.turnos.forEach((turnoGroup) => {
            // Fila de Turno
            const tRow = ws.addRow(['', turnoGroup.turno, '', '', '', turnoGroup.total]);
            tRow.eachCell((cell) =>
                styleCell(cell, { bgArgb: turnoBg, textArgb: turnoText, bold: true, size: 10 })
            );
            tRow.getCell(6).alignment = { horizontal: 'right', vertical: 'middle' };
            tRow.getCell(6).numFmt = '#,##0';
            tRow.height = 18;

            // Filas de detalle
            turnoGroup.orders.forEach((order) => {
                const dRow = ws.addRow([
                    '',
                    '',
                    order.ordenProd,
                    order.sku,
                    order.producto,
                    order.piezasProd || 0,
                ]);
                dRow.eachCell((cell) =>
                    styleCell(cell, {
                        bgArgb: 'FFFFFFFF',
                        textArgb: 'FF13151C',
                        bold: false,
                        size: 10,
                    })
                );
                dRow.getCell(6).alignment = { horizontal: 'right', vertical: 'middle' };
                dRow.getCell(6).numFmt = '#,##0';
                dRow.height = 16;
            });
        });
    });

    // Fila Total General
    const totRow = ws.addRow(['Total General', '', '', '', '', grandTotal]);
    totRow.eachCell((cell) =>
        styleCell(cell, { bgArgb: totalBg, textArgb: totalText, bold: true, size: 11 })
    );
    totRow.getCell(1).border = {
        top: { style: 'medium', color: { argb: `FF${primary.toUpperCase()}` } },
    };
    totRow.getCell(6).alignment = { horizontal: 'right', vertical: 'middle' };
    totRow.getCell(6).numFmt = '#,##0';
    totRow.getCell(6).border = {
        top: { style: 'medium', color: { argb: `FF${primary.toUpperCase()}` } },
    };
    totRow.height = 22;

    // Descargar
    const buffer = await wb.xlsx.writeBuffer();
    const fecha = new Date().toISOString().slice(0, 10);
    saveAs(
        new Blob([buffer], { type: 'application/octet-stream' }),
        `reporte_produccion_${fecha}.xlsx`
    );
}

// ─────────────────────────────────────────────────────────────────────────────

function groupByLinea(data) {
    const lineaMap = new Map();
    data.forEach((row) => {
        if (!lineaMap.has(row.linea)) {
            lineaMap.set(row.linea, { linea: row.linea, total: 0, turnos: new Map() });
        }
        const lineaGroup = lineaMap.get(row.linea);
        lineaGroup.total += row.piezasProd || 0;
        const turnoKey = row.turno;
        if (!lineaGroup.turnos.has(turnoKey)) {
            lineaGroup.turnos.set(turnoKey, { turno: turnoKey, total: 0, orders: [] });
        }
        const turnoGroup = lineaGroup.turnos.get(turnoKey);
        turnoGroup.total += row.piezasProd || 0;
        turnoGroup.orders.push(row);
    });

    return Array.from(lineaMap.values())
        .map((l) => ({
            ...l,
            turnos: Array.from(l.turnos.values()).sort((a, b) => a.turno - b.turno),
        }))
        .sort((a, b) => a.linea.localeCompare(b.linea));
}

function buildChartSeries(data) {
    const lineas = [...new Set(data.map((r) => r.linea))].sort();
    const skus = [...new Set(data.map((r) => r.sku))];

    const agg = {};
    data.forEach((row) => {
        const key = `${row.sku}||${row.linea}`;
        agg[key] = (agg[key] || 0) + (row.piezasProd || 0);
    });

    const series = skus.map((sku) => ({
        name: sku,
        data: lineas.map((linea) => agg[`${sku}||${linea}`] || 0),
    }));

    return { series, categories: lineas };
}

export default function InformeProduccion() {
    const { informeProduccionData, isLoadingLlamadas, txtRange, setExportFn } = useReportes();
    const [expandedLineas, setExpandedLineas] = useState({});
    const [expandedTurnos, setExpandedTurnos] = useState({});

    const grouped = useMemo(() => groupByLinea(informeProduccionData), [informeProduccionData]);

    useEffect(() => {
        if (grouped.length > 0) {
            setExportFn(() => () => exportarExcel(grouped, txtRange));
        } else {
            setExportFn(null);
        }
        return () => setExportFn(null);
    }, [grouped, txtRange, setExportFn]);
    const { series, categories } = useMemo(
        () => buildChartSeries(informeProduccionData),
        [informeProduccionData]
    );
    const chartColors = useMemo(() => getColors(series.length), [series.length]);

    const toggleLinea = (linea) => {
        setExpandedLineas((prev) => ({ ...prev, [linea]: prev[linea] === false ? true : false }));
    };

    const toggleTurno = (linea, turno) => {
        const key = `${linea}||${turno}`;
        setExpandedTurnos((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    };

    const chartOptions = useMemo(
        () => ({
            chart: {
                type: 'bar',
                stacked: true,
                toolbar: { show: false },
                fontFamily: 'WorkSans, sans-serif',
                animations: {
                    enabled: true,
                    easing: 'easeinout',
                    speed: 700,
                    animateGradually: { enabled: true, delay: 100 },
                    dynamicAnimation: { enabled: true, speed: 450 },
                },
            },
            colors: chartColors,
            plotOptions: {
                bar: { horizontal: false, columnWidth: '55%', borderRadius: 2 },
            },
            xaxis: {
                categories,
                labels: { style: { fontFamily: 'WorkSans, sans-serif', fontSize: '13px' } },
            },
            yaxis: {
                title: {
                    text: 'Piezas',
                    style: {
                        fontFamily: 'WorkSans, sans-serif',
                        fontSize: '12px',
                        color: '#5f606f',
                    },
                },
                labels: {
                    formatter: (v) => v.toLocaleString(),
                    style: { fontFamily: 'WorkSans, sans-serif', fontSize: '12px' },
                },
            },
            legend: {
                position: 'right',
                fontFamily: 'WorkSans, sans-serif',
                fontSize: '11px',
                offsetY: 10,
                itemMargin: { vertical: 2 },
            },
            fill: { opacity: 1 },
            title: {
                text: 'Producción por línea',
                align: 'center',
                offsetY: 4,
                style: {
                    fontFamily: 'WorkSans, sans-serif',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#13151c',
                },
            },
            subtitle: {
                text: txtRange ? `Periodo: ${txtRange}` : '',
                align: 'center',
                offsetY: 24,
                style: {
                    fontFamily: 'WorkSans, sans-serif',
                    fontSize: '12px',
                    color: '#5f606f',
                },
            },
            tooltip: {
                y: { formatter: (val) => `${val.toLocaleString()} pzs` },
            },
            dataLabels: { enabled: false },
            grid: { borderColor: '#e8ecf1' },
        }),
        [chartColors, categories, txtRange]
    );

    if (isLoadingLlamadas) {
        return (
            <div className="informe-produccion-loading">
                <CircularProgress className="informe-produccion-spinner" />
            </div>
        );
    }

    if (!informeProduccionData || informeProduccionData.length === 0) {
        return (
            <div className="informe-produccion-empty">
                <p>Selecciona los filtros y presiona &ldquo;Ver Reporte&rdquo;</p>
            </div>
        );
    }

    const grandTotal = grouped.reduce((acc, g) => acc + g.total, 0);

    return (
        <div className="informe-produccion-container">
            {/* Gráfica apilada por línea */}
            <div className="informe-produccion-chart-card">
                <ReactApexChart options={chartOptions} series={series} type="bar" height={320} />
            </div>

            {/* Toolbar tabla */}
            <div className="ip-table-toolbar">
                <span className="ip-table-title">Detalle de producción</span>
            </div>

            {/* Tabla jerárquica Línea > Turno > Órdenes */}
            <div className="informe-produccion-table-card">
                <Table size="small" className="informe-produccion-table">
                    <TableHead>
                        <TableRow className="ip-row-header">
                            <TableCell className="ip-header-cell ip-col-linea">Linea</TableCell>
                            <TableCell className="ip-header-cell ip-col-turno">Turno</TableCell>
                            <TableCell className="ip-header-cell ip-col-orden">
                                Orden Prod
                            </TableCell>
                            <TableCell className="ip-header-cell ip-col-sku">SKU</TableCell>
                            <TableCell className="ip-header-cell ip-col-producto">
                                Producto
                            </TableCell>
                            <TableCell className="ip-header-cell ip-col-piezas ip-align-right">
                                Piezas Prod
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {grouped.map((lineaGroup) => {
                            const lineaExpanded = expandedLineas[lineaGroup.linea] !== false;
                            return (
                                <React.Fragment key={lineaGroup.linea}>
                                    {/* Fila de Línea */}
                                    <TableRow className="ip-row-linea">
                                        <TableCell className="ip-cell-linea">
                                            <div className="ip-expand-cell">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => toggleLinea(lineaGroup.linea)}
                                                    className="ip-expand-btn"
                                                >
                                                    {lineaExpanded ? (
                                                        <ChevronDown size={14} />
                                                    ) : (
                                                        <ChevronRight size={14} />
                                                    )}
                                                </IconButton>
                                                <span>{lineaGroup.linea}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="ip-cell-linea" />
                                        <TableCell className="ip-cell-linea" />
                                        <TableCell className="ip-cell-linea" />
                                        <TableCell className="ip-cell-linea" />
                                        <TableCell className="ip-cell-linea ip-align-right">
                                            {lineaGroup.total.toLocaleString()}
                                        </TableCell>
                                    </TableRow>

                                    {lineaExpanded &&
                                        lineaGroup.turnos.map((turnoGroup) => {
                                            const turnoKey = `${lineaGroup.linea}||${turnoGroup.turno}`;
                                            const turnoExpanded =
                                                expandedTurnos[turnoKey] !== false;
                                            return (
                                                <React.Fragment key={turnoKey}>
                                                    {/* Fila de Turno */}
                                                    <TableRow className="ip-row-turno">
                                                        <TableCell className="ip-cell-turno" />
                                                        <TableCell className="ip-cell-turno">
                                                            <div className="ip-expand-cell">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                        toggleTurno(
                                                                            lineaGroup.linea,
                                                                            turnoGroup.turno
                                                                        )
                                                                    }
                                                                    className="ip-expand-btn"
                                                                >
                                                                    {turnoExpanded ? (
                                                                        <ChevronDown size={14} />
                                                                    ) : (
                                                                        <ChevronRight size={14} />
                                                                    )}
                                                                </IconButton>
                                                                <span>{turnoGroup.turno}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="ip-cell-turno" />
                                                        <TableCell className="ip-cell-turno" />
                                                        <TableCell className="ip-cell-turno" />
                                                        <TableCell className="ip-cell-turno ip-align-right">
                                                            {turnoGroup.total.toLocaleString()}
                                                        </TableCell>
                                                    </TableRow>

                                                    {/* Filas de detalle */}
                                                    {turnoExpanded &&
                                                        turnoGroup.orders.map((order) => (
                                                            <TableRow
                                                                key={order.ordenProd}
                                                                className="ip-row-detail"
                                                            >
                                                                <TableCell className="ip-cell-detail" />
                                                                <TableCell className="ip-cell-detail" />
                                                                <TableCell className="ip-cell-detail">
                                                                    {order.ordenProd}
                                                                </TableCell>
                                                                <TableCell className="ip-cell-detail">
                                                                    {order.sku}
                                                                </TableCell>
                                                                <TableCell className="ip-cell-detail">
                                                                    {order.producto}
                                                                </TableCell>
                                                                <TableCell className="ip-cell-detail ip-align-right">
                                                                    {(
                                                                        order.piezasProd || 0
                                                                    ).toLocaleString()}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                </React.Fragment>
                                            );
                                        })}
                                </React.Fragment>
                            );
                        })}

                        {/* Fila total general */}
                        <TableRow className="ip-row-total">
                            <TableCell className="ip-cell-total" colSpan={5}>
                                Total General
                            </TableCell>
                            <TableCell className="ip-cell-total ip-align-right">
                                {grandTotal.toLocaleString()}
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
